import { CopyObjectCommand, S3Client } from '@aws-sdk/client-s3';
import axios from 'axios';
import md5 from 'md5';
import SparkMD5 from 'spark-md5';
import { v4 as uuidv4 } from 'uuid';

import { fetchToken } from '@/services';
import { FileUploader } from '@/utils/uploader';

import {
  OSS_TYPE_USER_ASSET,
  OSS_TYPE_USER_AVATAR,
  OSS_TYPE_USER_PROJECT,
  OSS_TYPE_USER_TEMP,
  TIMEOUT,
} from './const';
import { isBCNWebSite, isBUSWebSite, isDev, isProdUs, isQA } from './envHelper';

// 判断是否是图片链接的辅助函数
const isImageUrl = (url: string): boolean => {
  if (!url) {
    return false;
  }
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
  const lowerUrl = url.toLowerCase();
  return typeof url === 'string' && imageExtensions.some((ext) => lowerUrl.endsWith(ext));
};

// 判断是否是视频链接的辅助函数
const isVideoUrl = (url: string): boolean => {
  if (!url) {
    return false;
  }
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return typeof url === 'string' && videoExtensions.some((ext) => lowerUrl.endsWith(ext));
};

const uploadFile = async (file: File) => {
  const uploader = FileUploader.getInstance();
  return await uploader.upload({
    file: file,
    tokenData: {
      fileName: file.name,
      dirSceneType: 20,
    },
  });
};

/**
 * 根据oss返回的上传路径，重新生成路径
 * @param filePath oss返回的path "20004-1/default/20231110/100053_1505872051490324480.prj"
 * @param fileName 工程文件名称 10053.prj
 */
export function renameOssUploadPath(
  filePath: string,
  fileName: string,
  keepFileName?: boolean,
) {
  let newFilePath = filePath;
  if (keepFileName) {
    let index = filePath.lastIndexOf('/');
    if (index !== -1) {
      let prefixPath = filePath.substring(0, index + 1);
      newFilePath = prefixPath + fileName;
    }
  }
  return newFilePath;
}

export const removeQueryParameters = (url: string) => {
  try {
    const urlObject = new URL(url);
    // 设置查询参数部分为空
    urlObject.search = '';
    return urlObject.toString();
  } catch (error) {
    // console.error('无效的URL', error);
    return url; // 如果URL无效，返回原始URL
  }
};

export function getFileExtensionFromUrl(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  const cleanUrl = removeQueryParameters(url);
  const filename = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);
  const extension = filename.split('.').pop();
  return extension?.toLowerCase();
}

/**
 * 获取文件名+后缀
 * @param url file/filename.png
 * @returns filename.png
 */
export const getFileName = (url: string = '') => {
  let index = url.lastIndexOf('/');
  if (index === -1) {
    index = 0;
  } else {
    index++;
  }
  return url.slice(index);
};

// 获取文件扩展名
export const getFileExt = (url: string = '') => {
  const dotIndex = url.lastIndexOf('.') + 1;
  const tempExt = url.slice(dotIndex);
  const qMarkIndex = tempExt.lastIndexOf('?');

  if (qMarkIndex === -1) return tempExt;

  return url.slice(dotIndex, qMarkIndex);
};

/**
 * 获取文件名
 * @param url file/filename.png
 * @returns filename
 */
export const getFileOnlyName = (url: string = '') => {
  let index = url.lastIndexOf('/');
  if (index === -1) {
    index = 0;
  } else {
    index++;
  }
  const dotIndex = url.lastIndexOf('.');

  if (dotIndex === -1) return url.slice(index);
  return url.slice(index, dotIndex);
};

// 处理上传的文件名
export const getSafeFileName = (fileName: string = '') => {
  const ext = getFileExt(fileName);

  if (fileName.length >= 64) {
    return `${Date.now()}.${ext}`;
  }

  const name = getFileOnlyName(fileName);

  const safeName = name.replace(/[^a-zA-Z0-9]+/g, '') || Date.now();

  return `${safeName}.${ext}`;
};

// 处理上传的文件名
export const getSafeFileNameWithoutExt = (fileName: string = '') => {
  if (fileName.length >= 64) {
    return `${Date.now()}`;
  }

  const name = getFileOnlyName(fileName);

  const safeName = name.replace(/[^a-zA-Z0-9]+/g, '') || Date.now();

  return `${safeName}`;
};

/**
 * 获取文件上传路径
 * @param fileName
 * @returns 用户资产-20 工程文件30 50-临时资产
 */

export type FileDirType = 'avatar' | 'asset' | 'prj' | 'temp';

export function getFileDisSceneType(type: FileDirType) {
  let dir: number;
  switch (type) {
    case 'avatar':
      dir = OSS_TYPE_USER_AVATAR;
      break;
    case 'asset':
      dir = OSS_TYPE_USER_ASSET;
      break;
    case 'prj':
      dir = OSS_TYPE_USER_PROJECT;
      break;
    case 'temp':
      dir = OSS_TYPE_USER_TEMP;
      break;
    default:
      dir = OSS_TYPE_USER_TEMP;
  }
  return dir;
}

/**
 * 获取音频时长
 * @param file
 * @param callback
 */
export const getAudioDurationMS = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.src = url; // 替换为你的音频文件 URL

    audio.addEventListener('loadedmetadata', () => {
      resolve(audio.duration * 1000);
    });
    audio.addEventListener('error', () => {
      reject();
    });
  });
};

export const getAudioDurationSeconds = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.setAttribute('crossOrigin', 'anonymous');
    audio.src = URL.createObjectURL(file);
    audio.addEventListener('loadedmetadata', () => {
      resolve(audio.duration);
    });
    audio.addEventListener('error', () => {
      reject();
    });
  });
};

/**
 * 获取上传的视频时长，返回毫秒
 * @param file
 * @param callback
 */
export function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    // 创建一个临时的视频元素
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';

    // 设置视频文件源
    video.src = URL.createObjectURL(file);
    // 监听加载元数据的事件
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      // 当视频的元数据已加载后，获取视频时长
      // 换算成毫秒
      //  callback(video.duration * 1000);
      resolve(video.duration * 1000);
    };
    video.onerror = function () {
      reject(-1);
    };
  });
}

/**
 * 将 File 文件名和时间戳转换成 md5 的 uid
 * @param fileName 原始的 file.name
 * @param timestamp Date.now
 * @returns md5 之后的 uid
 */
export function encodeFileId(fileName: string, timestamp: number): string {
  return md5(fileName + timestamp.toString());
}

/**
 * 上传时获取视频信息
 * @param file
 * @returns
 */
export const loadVideoInfo = async (file: File): Promise<HTMLVideoElement> => {
  return new Promise((resolve, reject) => {
    // 创建一个临时的视频元素
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';

    // 设置视频文件源
    video.src = URL.createObjectURL(file);
    // 监听加载元数据的事件
    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      resolve(video);
    };
    video.onerror = function () {
      reject(-1);
    };
  });
};

/**
 * 获取工程文件地址
 * @param puid
 * @returns
 */
export function getProjectPath(puid: string | number): string {
  let projectPath = '/data/' + puid + '.prj';
  return projectPath;
}

// destination path: /data/id.ext
export function getAssetPath(id: string, url: string): string {
  if (url.includes('x-oss-process=image') || url.includes('imageMogr2/thumbnail')) {
    // 图片目前都为 WebP 格式
    return `/data/${getFileName(id)}`;
  }
  return `/data/${id}.${getFileExtensionFromUrl(url)}`;
}
/**
 * 为了前端阿里云上传文件
 * 工程文件buffer流转blob
 */
export const dataToBlob = (buffer: Buffer) => {
  // @ts-ignore
  return new Blob([buffer], { type: 'application/octet-stream' });
};

/**
 * @param url 视频url
 * @returns
 */

export const getVideoCoverUrl = async (
  source: string | File,
  fallbackVideoUrl?: string,
): Promise<string> => {
  // 1. 处理非视频情况
  if (!source && !fallbackVideoUrl) return '';

  const videoSource =
    typeof source === 'string' && isVideoUrl(source)
      ? source
      : typeof source === 'string' && fallbackVideoUrl
        ? fallbackVideoUrl
        : typeof source !== 'string'
          ? source
          : '';

  // ✅ 旧 OSS 快照参数兼容
  if (typeof source === 'string') {
    if (source.includes('x-oss-process=video/snapshot')) return source;
    if (isImageUrl(source)) return source;
    if (!isProdUs && isVideoUrl(source)) {
      return `${source}?x-oss-process=video/snapshot,ss_0,t_0,f_jpg,w_0,h_0,m_fast,ar_auto`;
    }
  }

  // ✅ 本地 File 或需要截图的 URL
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = 'anonymous';
  video.src =
    typeof videoSource === 'string' ? videoSource : URL.createObjectURL(videoSource);

  const canvas = document.createElement('canvas');
  let ctx = canvas.getContext('2d');

  const cleanUp = () => {
    video.remove();
    if (typeof videoSource !== 'string') {
      URL.revokeObjectURL(video.src);
    }
    canvas.width = 0;
    canvas.height = 0;
    ctx = null;
  };

  // 改进的黑帧检测函数
  const isValidFrame = (ctx: CanvasRenderingContext2D, w: number, h: number): boolean => {
    const frame = ctx.getImageData(0, 0, w, h);
    const pixels = frame.data;

    // 采样检测（最多检测1000个像素点）
    const sampleSize = Math.min(1000, pixels.length / 4);

    let darkPixels = 0; // 暗像素计数
    let lightPixels = 0; // 亮像素计数
    let colorVariance = 0; // 颜色方差（用于检测图像复杂度）
    const darkThreshold = 10; // 暗像素阈值
    const lightThreshold = 245; // 亮像素阈值

    for (let i = 0; i < sampleSize; i++) {
      const pixelIndex = Math.floor(Math.random() * (pixels.length / 4)) * 4;
      const r = pixels[pixelIndex];
      const g = pixels[pixelIndex + 1];
      const b = pixels[pixelIndex + 2];

      // 计算颜色方差（RGB与平均值的差异）
      const avg = (r + g + b) / 3;
      colorVariance += Math.abs(r - avg) + Math.abs(g - avg) + Math.abs(b - avg);

      // 统计暗像素和亮像素
      if (r < darkThreshold && g < darkThreshold && b < darkThreshold) {
        darkPixels++;
      } else if (r > lightThreshold && g > lightThreshold && b > lightThreshold) {
        lightPixels++;
      }
    }

    // 计算比例和平均方差
    const avgVariance = colorVariance / sampleSize;
    const darkRatio = darkPixels / sampleSize;
    const lightRatio = lightPixels / sampleSize;

    // 判断有效性：暗像素比例<98% AND 亮像素比例<98% AND 有足够的颜色变化
    return darkRatio < 0.98 && lightRatio < 0.98 && avgVariance > 5;
  };

  const captureFrame = (): Promise<Blob | null> => {
    return new Promise((resolve, reject) => {
      let frameId: number | null = null;
      let timeoutId: NodeJS.Timeout;
      let startTime = Date.now();
      const maxDuration = 5000;
      const minCheckTime = 500;

      const cleanup = () => {
        if (frameId !== null) {
          if ('cancelVideoFrameCallback' in video) {
            (video as any).cancelVideoFrameCallback(frameId);
          } else {
            cancelAnimationFrame(frameId);
          }
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        video.pause();
      };

      // 首先尝试截取静态的第一帧
      const captureStaticFirstFrame = () => {
        if (!ctx) {
          return false;
        }

        try {
          // 确保视频在第一帧位置
          video.currentTime = 0;

          // 等待一小段时间确保帧更新
          setTimeout(() => {
            // 检查画布上下文是否仍然有效
            if (!ctx) {
              startPlaybackCapture();
              return;
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0);

            // 检查是否为有效帧
            if (isValidFrame(ctx, canvas.width, canvas.height)) {
              cleanup();
              canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
              return; // 修复：这里应该直接返回，不需要return true
            }

            // 如果第一帧是黑帧，则开始播放寻找有效帧
            startPlaybackCapture();
          }, 100);

          return true;
        } catch (error) {
          console.warn('Static frame capture failed:', error);
          return false;
        }
      };

      // 播放模式的帧捕获
      const startPlaybackCapture = () => {
        let frameCheckCount = 0; // 添加帧检查计数器
        const checkFrame = () => {
          const elapsed = Date.now() - startTime;
          const currentTime = video.currentTime;
          frameCheckCount++;

          if (!ctx) {
            cleanup();
            reject(new Error('Canvas context lost'));
            return;
          }

          try {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0);

            if (isValidFrame(ctx, canvas.width, canvas.height)) {
              console.log(
                `🎉 找到有效帧！时间: ${currentTime.toFixed(2)}s, 总检测次数: ${frameCheckCount}, 总耗时: ${elapsed}ms`,
              );
              cleanup();
              canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
              return;
            }

            if (elapsed >= maxDuration) {
              console.warn(
                `⏰ 检测超时(${elapsed}ms)，使用当前帧 - 时间: ${currentTime.toFixed(2)}s, 检测次数: ${frameCheckCount}`,
              );
              cleanup();
              canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
              return;
            }

            if (elapsed < minCheckTime) {
              setTimeout(() => {
                if ('requestVideoFrameCallback' in video) {
                  frameId = (video as any).requestVideoFrameCallback(checkFrame);
                } else {
                  frameId = requestAnimationFrame(checkFrame);
                }
              }, 50);
            } else {
              if ('requestVideoFrameCallback' in video) {
                frameId = (video as any).requestVideoFrameCallback(checkFrame);
              } else {
                frameId = requestAnimationFrame(checkFrame);
              }
            }
          } catch (error) {
            console.error('❌ 帧检测过程中出错:', error);
            cleanup();
            reject(error);
          }
        };

        // 开始播放并截帧
        video
          .play()
          .then(() => {
            startTime = Date.now();

            if ('requestVideoFrameCallback' in video) {
              frameId = (video as any).requestVideoFrameCallback(checkFrame);
            } else {
              frameId = requestAnimationFrame(checkFrame);
            }

            timeoutId = setTimeout(() => {
              console.warn(`⏰ 全局超时(${maxDuration + 1000}ms)，强制使用当前帧`);
              cleanup();
              if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(video, 0, 0);
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
              } else {
                reject(new Error('Capture timeout - no context'));
              }
            }, maxDuration + 1000);
          })
          .catch((playError) => {
            console.warn('Video play failed, trying muted:', playError);
            video.muted = true;
            video
              .play()
              .then(() => {
                startTime = Date.now();
                if ('requestVideoFrameCallback' in video) {
                  frameId = (video as any).requestVideoFrameCallback(checkFrame);
                } else {
                  frameId = requestAnimationFrame(checkFrame);
                }
                timeoutId = setTimeout(() => {
                  cleanup();
                  if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(video, 0, 0);
                    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
                  } else {
                    reject(new Error('Capture timeout - no context'));
                  }
                }, maxDuration + 1000);
              })
              .catch((mutedPlayError) => {
                console.error('Muted play also failed:', mutedPlayError);
                cleanup();
                reject(new Error('Cannot play video'));
              });
          });
      };

      // 确保视频可以播放
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        cleanup();
        reject(new Error('Video not ready'));
        return;
      }

      // 首先尝试截取静态第一帧
      if (!captureStaticFirstFrame()) {
        // 如果静态截取失败，直接开始播放模式
        startPlaybackCapture();
      }
    });
  };

  try {
    // 等待视频加载
    await new Promise<void>((resolve, reject) => {
      const onLoaded = () => {
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onError);
        resolve();
      };

      const onError = () => {
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onError);
        reject(new Error('Video load failed'));
      };

      video.addEventListener('loadeddata', onLoaded, { once: true });
      video.addEventListener('error', onError, { once: true });
    });

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // 直接截帧，不再 seek 到特定时间点
    const blob = await captureFrame();

    if (!blob) {
      throw new Error('Failed to capture frame');
    }

    // 上传截图
    const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' });

    const coverUrl = await compressImageForCover(file);

    if (!coverUrl) {
      throw new Error('Upload failed');
    }

    return coverUrl;
  } catch (err) {
    console.error('getVideoCoverUrl error:', err);
    return '';
  } finally {
    cleanUp();
  }
};

export const fileToArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

export const getSafeFileNameV2 = (fileName: string = '') => {
  console.log('get fileName', fileName);
  const ext = getFileExt(fileName);

  console.log('get ext', ext);

  if (fileName.length >= 80) {
    return `${Date.now()}.${ext}`;
  }

  const name = getFileOnlyName(fileName);

  const safeName = name.replace(/[^a-zA-Z0-9]+/g, '') || Date.now();

  return `${safeName}.${ext}`;
};

// 获取素材标题
export const getAssetTitle = (url: string = '') => {
  const dotIndex = url.lastIndexOf('.');
  let name = url.slice(0, dotIndex);
  return name || url;
};

/**
 * 时长展示
 * @param duration 192秒 or 192000
 * @param milliseconds true=毫秒
 * @returns 00:12
 */

export const formatDuration = (time: number, millisecond?: boolean): string => {
  let duration = time;
  if (!duration) return '00:00';
  if (millisecond) {
    duration = duration / 1000;
  }
  let seconds = Math.floor(duration % 60);
  let minutes = Math.floor((duration - seconds) / 60);

  let mm = minutes.toString();
  let ss = seconds.toString();
  if (minutes < 10) mm = `0${mm}`;
  if (seconds < 10) ss = `0${ss}`;
  return `${mm}:${ss}`;
};

/**
 * 工程时长转化
 * @return 45.7 or 02:30
 */

export const formatDurationPrj = (timeLength: number): string => {
  if (!timeLength) return '0';
  let duration = timeLength / 1000;
  let seconds = duration % 60;
  let minutes = Math.floor((duration - seconds) / 60);
  let timeString = ``;

  if (minutes > 0) {
    let mm = minutes.toString();
    if (minutes < 10) mm = `0${mm}`;
    timeString = `${mm}:${Math.floor(seconds)}`;
  } else {
    timeString = `${seconds.toFixed(1)}s`;
  }

  return timeString;
};

/**
 * 工程时长转化
 * @return 02:30 to 45.7
 */
export const formatDurationToSecond = (timeLength: number): string => {
  if (!timeLength) return '0';
  let duration = timeLength / 1000;

  return `${duration.toFixed(1)}s`;
};

/**
 *
 * @param fileOrUrl
 * @returns 获取视频信息
 */
export const getVideoInfo = (
  fileOrUrl: Blob | string,
): Promise<{
  width: number;
  height: number;
  duration: string;
  type: string;
}> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    // 判断传入的是 Blob 还是 URL
    let type = '';
    if (fileOrUrl instanceof Blob) {
      video.src = URL.createObjectURL(fileOrUrl);
      type = fileOrUrl.type;
    } else if (typeof fileOrUrl === 'string') {
      video.src = fileOrUrl;
      type = fileOrUrl.split('.').pop() as string;
    }

    video.oncanplay = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: Number(video.duration).toFixed(3),
        type,
      });
      video.remove();
    };
  });
};

// export const getImageWH = (file: any) => {
//   new Promise(function (resolve, reject) {
//     let img: any = new Image();
//     img.src = URL.createObjectURL(file);
//     let width, height;
//     img.onload = () => resolve(img.width);
//   });
// };
export const getImageWH = (path: any): Promise<any> => {
  return new Promise(function (resolve, reject) {
    let img: any = new Image();
    img.src = URL.createObjectURL(path);
    let width, height;
    img.onload = () => resolve({ width: img.width, height: img.height });
  });
};

/**
 * 压缩图片生成封面图
 * @param file 原始图片文件
 * @param maxWidth 最大宽度，默认 1920
 * @param maxHeight 最大高度，默认 1080
 * @param quality 压缩质量 0-1，默认 0.8
 * @returns 压缩后的图片 URL
 */
export const compressImageForCover = async (
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1080,
  quality: number = 0.8,
): Promise<string> => {
  // 根据文件大小调整压缩策略
  const fileSizeMB = file.size / (1024 * 1024);

  // 超大文件使用更激进的压缩参数
  if (fileSizeMB > 100) {
    // 100MB+ 文件
    maxWidth = 1280;
    maxHeight = 720;
    quality = 0.6;
  } else if (fileSizeMB > 50) {
    // 50-100MB 文件
    maxWidth = 1600;
    maxHeight = 900;
    quality = 0.7;
  } else if (fileSizeMB > 20) {
    // 20-50MB 文件
    quality = 0.75;
  }

  return new Promise((resolve, reject) => {
    // 设置超时，防止超大文件卡死
    const timeoutId = setTimeout(() => {
      reject(new Error('Image compression timeout'));
    }, 60000); // 60秒超时

    // 使用 createImageBitmap 替代 Image，性能更好，内存占用更低
    createImageBitmap(file)
      .then(async (bitmap) => {
        clearTimeout(timeoutId);

        try {
          let { width, height } = bitmap;

          // 如果图片尺寸在限制范围内且文件不大，直接返回原图
          if (width <= maxWidth && height <= maxHeight && fileSizeMB <= 5) {
            bitmap.close();
            const coverUrl = await uploadFile(file);
            resolve(coverUrl || '');
            return;
          }

          // 计算缩放比例，保持宽高比
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio, 1); // 不放大图片

          const newWidth = Math.floor(width * ratio);
          const newHeight = Math.floor(height * ratio);

          // 使用 OffscreenCanvas（如果支持）或普通 Canvas
          let canvas: HTMLCanvasElement | OffscreenCanvas;
          let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;

          if (typeof OffscreenCanvas !== 'undefined') {
            canvas = new OffscreenCanvas(newWidth, newHeight);
            ctx = canvas.getContext('2d');
          } else {
            canvas = document.createElement('canvas');
            canvas.width = newWidth;
            canvas.height = newHeight;
            ctx = canvas.getContext('2d');
          }

          if (!ctx) {
            bitmap.close();
            reject(new Error('Canvas context not available'));
            return;
          }

          // 绘制压缩后的图片
          ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
          bitmap.close(); // 释放内存

          // 转换为 Blob
          const blobPromise =
            canvas instanceof OffscreenCanvas
              ? canvas.convertToBlob({ type: 'image/jpeg', quality })
              : new Promise<Blob | null>((res) =>
                  (canvas as HTMLCanvasElement).toBlob(res, 'image/jpeg', quality),
                );

          const blob = await blobPromise;

          // 清理 canvas
          if (canvas instanceof HTMLCanvasElement) {
            canvas.width = 0;
            canvas.height = 0;
          }

          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          // 创建压缩后的文件
          const compressedFile = new File([blob], `cover_${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });

          // 上传压缩后的图片
          const coverUrl = await uploadFile(compressedFile);
          resolve(coverUrl || '');
        } catch (error) {
          reject(error);
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(new Error(`Failed to load image: ${error.message}`));
      });
  });
};

export const renameBlob = (blob: Blob, newName: string) => {
  const file = new File([blob], newName, { type: blob.type });
  return file;
};

// 数字换算文件大小
export const calculateFileBytes = (bytes: number | undefined): string => {
  if (!bytes) {
    return '-';
  }
  if (bytes < 1024) {
    return bytes + ' B'; // 字节
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + ' KB'; // KB，保留两位小数
  } else if (bytes < 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'; // MB，保留两位小数
  } else {
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB'; // GB，保留两位小数
  }
};

/**
 * 获取上传的视频时长
 * @param file
 * @param callback
 */
export const getAudioDuration = (url: string) => {
  const audio = new Audio();
  audio.src = url; // 替换为你的音频文件 URL

  audio.onloadedmetadata = () => {
    console.log('音频时长:', audio.duration + '秒');
  };
};

export const fetchBlob = async (url: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.blob();
  } catch (error) {
    console.error('Error fetching blob:', error);
    throw error;
  }
};

/**
 * 获取音频信息
 */
export const getAudioInfo = async (audioUrl: string): Promise<any> => {
  try {
    const blob = await axios.get(audioUrl, {
      responseType: 'blob',
      validateStatus: (state: number) => state < 500,
    });

    return blob;
  } catch (error) {
    // 处理错误
    console.error('There was a problem with your fetch operation:', error);
    throw error;
  }
};

/**
 * 下载远程url的json文件
 * @param url
 * @param dbPath
 * @param onProgress
 * @param isOverwrite
 */
/**
 * 从远程URL下载JSON数据
 * @param url 要下载的JSON数据的URL
 */
export const downloadJsonData = async (url: string): Promise<any> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const jsonData = await response.json();
    return jsonData;
  } catch (error) {
    return { isError: true, message: '下载JSON数据时出错' };
  }
};

/**
 * 获取文件后缀名
 */
export function getFileExtension(filename: string) {
  if (!filename) {
    return '';
  }
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop() : '';
}

/**
 * 获取媒体文件时长
 * @param url 媒体文件URL
 * @returns Promise<number> 返回时长(秒)
 */
export const getMediaDuration = (mediaInput: string | File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const getMediaType = (input: string | File) => {
      const ext =
        typeof input === 'string'
          ? input.split('.').pop()?.toLowerCase()
          : input.name.split('.').pop()?.toLowerCase();

      const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'm4v', 'avi', 'wmv', 'flv', 'mkv'];
      const audioExts = ['mp3', 'wav', 'aac', 'm4a', 'wma', 'ogg', 'flac'];

      if (ext && videoExts.includes(ext)) return 'video';
      if (ext && audioExts.includes(ext)) return 'audio';

      return 'audio';
    };

    const mediaType = getMediaType(mediaInput);
    const media = mediaType === 'audio' ? new Audio() : document.createElement('video');
    media.preload = 'metadata';

    const handleError = (e: ErrorEvent) => {
      cleanup();
      reject(new Error(`Failed to load media: ${e.message}`));
    };

    const handleLoadedMetadata = () => {
      const duration = media.duration;
      cleanup();
      resolve(isNaN(duration) ? 0 : duration);
    };

    const cleanup = () => {
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
      media.removeEventListener('error', handleError);
      media.src = '';
      if (mediaType === 'video') {
        (media as HTMLVideoElement).remove();
      }
      if (typeof mediaInput !== 'string') {
        URL.revokeObjectURL(media.src);
      }
    };

    media.addEventListener('loadedmetadata', handleLoadedMetadata);
    media.addEventListener('error', handleError);

    media.src =
      typeof mediaInput === 'string' ? mediaInput : URL.createObjectURL(mediaInput);
  });
};

/**
 * 1.移除文件名中的特殊字符
 *  #, ?, @, &, =, /, \, *, :, ", <, >, |, +, ,, ;, {, }, [, ], ^, %
 * 2.将多个空格替换为单个下划线
   3.去除首尾的空格
 * @param fileName - 要处理的文件名
 * @returns 移除特殊字符后的文件名
 */
export function removeSpecialCharacter(fileName: string) {
  return (
    fileName
      // 移除特定的特殊字符，这些字符可能在文件名中导致问题
      .replace(/[#?@&=/\\:*"<>|+,;{}[\]^%]/g, '')
      // 将连续的空格替换为单个下划线
      .replace(/\s+/g, '_')
      // 去除首尾空格
      .trim()
  );
}

export default async function downloadFiles(
  files: { fileUrl: string; fileName: string }[],
) {
  // const handleDownload = async () => {
  // const files = [
  //   { fileUrl: 'https://example.com/file1.txt', fileName: 'file1.txt' },
  //   { fileUrl: 'https://example.com/file2.pdf', fileName: 'file2.pdf' },
  // ];

  try {
    const response = await fetch('/api/batchDownload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        files,
      }),
    });
    const data = await response.json();

    const blob = new Blob([data], { type: 'application/zip' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'files.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Download failed:', error);
  }
}
/**
 * 流式下载文件
 */
interface FileInfo {
  fileUrl: string;
  fileName: string;
}

interface DownloadConfig {
  clickDelay?: number; // 点击延迟(ms)
  cleanupDelay?: number; // 清理延迟(ms)
  maxConcurrency?: number; // 最大并发数
}
function getDownloadHost() {
  if (isBCNWebSite) {
    return 'fetch-chn.boomcutai.com';
  } else if (isBUSWebSite) {
    return 'fetch-us.boomcut.ai';
  } else {
    return 'fetch-us.falcocut.com';
  }
}

// 新打开页面下载
export function downloadFileByBlank(
  filesInput: FileInfo | FileInfo[],
  config: DownloadConfig = {},
) {
  const { cleanupDelay = 1000, clickDelay = 2000 } = config;

  const files = Array.isArray(filesInput) ? filesInput : [filesInput];

  const downloadFile = (file: FileInfo) => {
    return new Promise<void>((resolve) => {
      window.open(file.fileUrl, '_blank');

      setTimeout(() => {
        resolve();
      }, cleanupDelay);
    });
  };

  const downloadFilesSequentially = async () => {
    for (const file of files) {
      await downloadFile(file);
      await new Promise((resolve) => setTimeout(resolve, clickDelay));
    }
  };

  downloadFilesSequentially();
}

export function downloadStreamFile(
  filesInput: FileInfo | FileInfo[],
  config: DownloadConfig = {},
) {
  if (isQA || isDev) {
    downloadFileByBlank(filesInput, config);
    return;
  }
  const {
    cleanupDelay = 1000 * 60 * 30, // 30分钟，确保排队的请求不会被 kill
    clickDelay = 1000,
    maxConcurrency = 1,
  } = config;

  const files = Array.isArray(filesInput) ? filesInput : [filesInput];

  const downloadSingleFile = (file: FileInfo): Promise<void> => {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.height = '0';
      iframe.style.width = '0';
      iframe.style.border = '0';

      let downloadUrl = file.fileUrl;

      try {
        const urlObj = new URL(file.fileUrl);
        if (downloadUrl.includes('static-us.falcocut.com')) {
          urlObj.host = getDownloadHost();
          urlObj.protocol = 'https:';
        }

        // 智能处理后缀
        const ext = file.fileUrl.split('.').pop() || '';
        const fileNameParam = file.fileName.endsWith(`.${ext}`)
          ? file.fileName
          : `${file.fileName}.${ext}`;

        urlObj.searchParams.set('fileName', fileNameParam);
        downloadUrl = urlObj.toString();
      } catch (e) {
        console.warn('URL construction failed, using original', e);
      }

      // 触发下载
      iframe.src = downloadUrl;
      document.body.appendChild(iframe);

      // 延迟清理 iframe
      // 只有当 iframe 存在时，浏览器才会维持 Pending 状态的请求
      setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, cleanupDelay);

      // 关键修改：不要立即 resolve，而是等待 clickDelay 后再 resolve
      // 这样可以控制 iframe 插入 DOM 的速率，而不是瞬间全部插入
      if (clickDelay > 0) {
        setTimeout(() => {
          resolve();
        }, clickDelay);
      } else {
        resolve();
      }
    });
  };

  // 并发队列控制器
  const queue = [...files];
  let activeCount = 0;

  const next = async () => {
    // 队列为空且没有活动任务时结束
    if (queue.length === 0 && activeCount === 0) return;

    while (activeCount < maxConcurrency && queue.length > 0) {
      const file = queue.shift();
      if (file) {
        activeCount++;
        try {
          await downloadSingleFile(file);
        } catch (e) {
          console.error('Download trigger failed', e);
        } finally {
          activeCount--;
          // 递归处理下一个
          next();
        }
      }
    }
  };

  // 启动下载队列
  next();
}

/**
 * 给url的文件名添加后缀
 * @param url 文件url
 * @param suffix 后缀
 * @returns 新文件url
 */
function getFileNameWithSuffix(url: string, suffix: string) {
  const target = url.split('/').pop();

  if (!target) {
    return `${uuidv4()}`; // 兜底返回时间戳 + UUID
  }

  const lastDotIndex = target.lastIndexOf('.');

  if (lastDotIndex === -1) {
    // 无扩展名情况
    return `${target}_${suffix}`;
  }

  // 插入后缀
  const fileName = target.slice(0, lastDotIndex);
  const extension = target.slice(lastDotIndex);
  return `${fileName}_${suffix}${extension}`;
}

function getDomainAndPath(url: string) {
  const match = url.match(/^(https?:\/\/[^\/]+)(\/.*)?$/);
  if (match) {
    return {
      domain: match[1], // 域名部分
      path: match[2] ? match[2].slice(1) : '', // 路径部分（默认为根路径 "/")
    };
  }
  return null;
}

function replaceDomain(url: string, newDomain: string) {
  return url.replace(/^(https?:\/\/[^\/]+)/, newDomain);
}

/**
 * 从OSS复制文件
 * @param originFileUrl 原始文件URL
 * @returns 新url
 */
export async function ossCopy(originFileUrl: string) {
  try {
    const fileName = getFileNameWithSuffix(originFileUrl, 'copy');
    const originFileDic = getDomainAndPath(originFileUrl);
    if (!originFileDic?.domain || !originFileDic?.path) {
      throw new Error('无效的OSS URL路径');
    }
    const dirSceneType = 20;
    const data = await fetchToken(fileName, dirSceneType);
    if (data.code !== 200 || !data.data) return null;
    const {
      uploadHost,
      bucket,
      region,
      accessKey,
      accessSecret,
      securityToken,
      filePath,
      ossType,
    } = data.data;
    if (ossType === 'ali') {
      const OSS = (await import('ali-oss')).default;
      const { host } = new URL(uploadHost);
      const client = new OSS({
        timeout: TIMEOUT,
        endpoint: `https://${bucket}.${host}`,
        region,
        accessKeyId: accessKey,
        accessKeySecret: accessSecret,
        stsToken: securityToken,
        bucket,
        cname: true,
      });

      const response = await client.copy(filePath, originFileDic.path);
      // @ts-ignore
      const newUrl = response?.res?.requestUrls[0] as string;
      return newUrl ? replaceDomain(newUrl, originFileDic.domain) : null;
    } else {
      const client = new S3Client({
        region,
        credentials: {
          accessKeyId: accessKey,
          secretAccessKey: accessSecret,
          sessionToken: securityToken,
        },
      });
      if (!client) {
        throw new Error('Get AWS Client Error');
      }
      const command = new CopyObjectCommand({
        Bucket: bucket,
        CopySource: `${bucket}/${originFileDic.path}`,
        Key: filePath,
      });
      if (!command) {
        throw new Error('Get AWS Client Error');
      }
      const result = await client.send(command);
      if (result?.CopyObjectResult?.ETag) {
        return `${originFileDic.domain}/${filePath}`;
      } else {
        return null;
      }
    }
  } catch (error) {
    console.error(error);
  }
}

export const GetFileMD5 = (file: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    let filePrototype: any = File.prototype;
    const blobSlice =
      File.prototype.slice || filePrototype.mozSlice || filePrototype.webkitSlice;
    const chunkSize = 2097152; // 2MB

    const fileReader = new FileReader();
    const spark = new SparkMD5.ArrayBuffer();

    let currentChunk = 0;
    const chunks = Math.ceil(file.size / chunkSize);

    fileReader.onload = (e: any) => {
      spark.append(e.target.result); // Append array buffer
      currentChunk++;

      if (currentChunk < chunks) {
        loadNext();
      } else {
        resolve(spark.end());
      }
    };

    fileReader.onerror = (e) => {
      reject(e);
    };

    const loadNext = () => {
      const start = currentChunk * chunkSize;
      const end = start + chunkSize >= file.size ? file.size : start + chunkSize;

      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    };

    loadNext();
  });
};

export const getVideoExtension = (
  file: File | null | undefined,
  url: string,
): 'mp4' | 'mov' => {
  if (file) {
    if (file.type) {
      if (file.type.includes('quicktime') || file.type.includes('mov')) {
        return 'mov';
      }
      if (file.type.includes('mp4')) {
        return 'mp4';
      }
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension === 'mov') {
      return 'mov';
    }

    return 'mp4';
  }

  const urlLower = url.toLowerCase();
  const urlParts = urlLower.split('/');
  const fileName = urlParts[urlParts.length - 1].split('?')[0];

  if (fileName.endsWith('.mov')) {
    return 'mov';
  }

  return 'mp4';
};

export const getImageInfo = (
  data: string | File,
): Promise<{ width: number; height: number; coverUrl: string }> => {
  return new Promise((resolve, reject) => {
    if (typeof data === 'string') {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // 处理跨域
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          coverUrl: data,
        });
      };
      img.onerror = (e) => {
        reject(e);
      };
      img.src = data;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height,
            coverUrl: e.target?.result as string,
          });
        };
        img.onerror = (e) => {
          reject(e);
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsDataURL(data);
    }
  });
};
